import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { ClockIcon, CalendarIcon, TagIcon } from '@heroicons/react/24/outline';
import { getHealthHackBySlug, getAllHealthHacks, HealthHack } from '../services/api';
import { useScrollToTop } from '../hooks/useScrollToTop';
import ShareButtons from '../components/ShareButtons';

const HealthHackPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [hack, setHack] = useState<HealthHack | null>(null);
  const [relatedHacks, setRelatedHacks] = useState<HealthHack[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Scroll to top when health hack changes
  useScrollToTop();

  // Function to clean content by removing code blocks and fixing formatting
  const cleanContent = (content: string): string => {
    if (!content) return '';
    
    // Remove HTML code blocks (```html ... ```)
    let cleaned = content.replace(/```html\s*([\s\S]*?)\s*```/g, '$1');
    
    // Remove any remaining code block markers
    cleaned = cleaned.replace(/```[\s\S]*?```/g, '');
    
    // Remove any leading/trailing whitespace
    cleaned = cleaned.trim();
    
    // Fix any malformed HTML tags
    cleaned = cleaned.replace(/<([^>]+)>/g, (match, tagContent) => {
      // Ensure proper closing of self-closing tags
      if (tagContent.match(/^(br|hr|img|input|meta|link)$/i)) {
        return `<${tagContent} />`;
      }
      return match;
    });
    
    return cleaned;
  };

  useEffect(() => {
    const fetchHack = async () => {
      if (!slug) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const hackData = await getHealthHackBySlug(slug);
        
        if (hackData) {
          // Clean the content before setting it
          const cleanedHack = {
            ...hackData,
            content: cleanContent(hackData.content)
          };
          setHack(cleanedHack);
          
          // Fetch related hacks based on category and tags
          const allHacks = await getAllHealthHacks();
          const relatedHacks = allHacks
            .filter((h: HealthHack) => h.slug !== slug) // Exclude current hack
            .sort((a: HealthHack, b: HealthHack) => {
              let scoreA = 0;
              let scoreB = 0;
              
              // Same category gets highest priority
              if (a.category === hackData.category) scoreA += 10;
              if (b.category === hackData.category) scoreB += 10;
              
              // Shared tags get medium priority
              if (hackData.tags && a.tags) {
                const sharedTagsA = hackData.tags.filter(tag => a.tags.includes(tag)).length;
                scoreA += sharedTagsA * 5;
              }
              if (hackData.tags && b.tags) {
                const sharedTagsB = hackData.tags.filter(tag => b.tags.includes(tag)).length;
                scoreB += sharedTagsB * 5;
              }
              
              // Featured hacks get slight priority
              if (a.featured) scoreA += 2;
              if (b.featured) scoreB += 2;
              
              // Sort by score (highest first), then by date (newest first)
              if (scoreA !== scoreB) {
                return scoreB - scoreA;
              }
              return new Date(b.published_at).getTime() - new Date(a.published_at).getTime();
            })
            .slice(0, 3); // Get top 3 related hacks
          
          setRelatedHacks(relatedHacks);
        } else {
          setError('Daily tip not found');
        }
      } catch (err) {
        setError('Failed to load daily tip');
      } finally {
        setLoading(false);
      }
    };

    fetchHack();
  }, [slug]);

  const getCategoryName = (categoryId: string) => {
    const categories = {
      'stress-relief': 'Stress Relief',
      mindfulness: 'Mindfulness', 
      sleep: 'Sleep Better',
      'self-care': 'Self Care',
      wellness: 'Wellness'
    };
    return categories[categoryId as keyof typeof categories] || categoryId;
  };

  const getCategoryColor = (category: string) => {
    // Use consistent blue for all categories
    return 'category-badge';
  };

  // Helper function to safely get SEO keywords
  const getSeoKeywords = (hack: HealthHack): string[] => {
    if (Array.isArray(hack.seo_keywords)) {
      return hack.seo_keywords;
    }
    if (typeof hack.seo_keywords === 'string' && hack.seo_keywords) {
      return hack.seo_keywords.split(',').map(k => k.trim());
    }
    return [];
  };

  // Helper function to format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner h-12 w-12 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading daily tip...</p>
        </div>
      </div>
    );
  }

  if (error || !hack) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Daily Tip Not Found</h1>
          <p className="text-gray-600 mb-8">{error || 'The requested daily tip could not be found.'}</p>
          <Link
            to="/tips"
            className="btn-primary"
          >
            Browse All Daily Tips
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{hack.seo_title || hack.title} | Destress Daily</title>
        <meta name="description" content={hack.seo_description || hack.excerpt} />
        <meta name="keywords" content={getSeoKeywords(hack).join(', ')} />
        
        {/* Open Graph */}
        <meta property="og:title" content={hack.seo_title || hack.title} />
        <meta property="og:description" content={hack.seo_description || hack.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://daily-health-advice-vdw1-4ka6k1xwy-aidwork11s-projects.vercel.app/tip/${hack.slug}`} />
        <meta property="og:image" content="https://daily-health-advice-vdw1-4ka6k1xwy-aidwork11s-projects.vercel.app/og-image.svg" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={hack.seo_title || hack.title} />
        <meta name="twitter:description" content={hack.seo_description || hack.excerpt} />
        <meta name="twitter:image" content="https://daily-health-advice-vdw1-4ka6k1xwy-aidwork11s-projects.vercel.app/og-image.svg" />
        
        {/* Canonical URL */}
        <link rel="canonical" href={`https://daily-health-advice-vdw1-4ka6k1xwy-aidwork11s-projects.vercel.app/tip/${hack.slug}`} />
        
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": hack.title,
            "description": hack.excerpt,
            "image": "https://daily-health-advice-vdw1-4ka6k1xwy-aidwork11s-projects.vercel.app/og-image.svg",
            "author": {
              "@type": "Organization",
              "name": "Destress Daily"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Destress Daily",
              "logo": {
                "@type": "ImageObject",
                "url": "https://daily-health-advice-vdw1-4ka6k1xwy-aidwork11s-projects.vercel.app/favicon.svg"
              }
            },
            "datePublished": hack.published_at,
            "dateModified": hack.published_at,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://daily-health-advice-vdw1-4ka6k1xwy-aidwork11s-projects.vercel.app/tip/${hack.slug}`
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Breadcrumbs */}
        <nav className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
            <div className="flex items-center space-x-2 text-xs text-gray-600">
              <Link to="/" className="hover:text-primary-600">Home</Link>
              <span>/</span>
              <Link to="/tips" className="hover:text-primary-600">Daily Tips</Link>
              <span>/</span>
              <span className="text-gray-900">{hack.title}</span>
            </div>
          </div>
        </nav>

        <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Article Header */}
          <header className="mb-8">
            <div className="mb-4">
              <Link
                to={`/category/${hack.category}`}
                className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(hack.category)} hover:opacity-80 transition-opacity`}
              >
                {getCategoryName(hack.category)}
              </Link>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              {hack.title}
            </h1>
            
            <p className="text-xl text-gray-600 mb-6 leading-relaxed">
              {hack.excerpt}
            </p>
            
            <div className="flex items-center space-x-6 text-sm text-gray-500">
              <div className="flex items-center">
                <ClockIcon className="h-4 w-4 mr-2" />
                {hack.read_time} min read
              </div>
              <div className="flex items-center">
                <CalendarIcon className="h-4 w-4 mr-2" />
                {formatDate(hack.published_at)}
              </div>
            </div>
          </header>

          {/* Article Content */}
          <div className="prose prose-lg max-w-none mb-12">
            <div dangerouslySetInnerHTML={{ __html: hack.content }} />
          </div>

          {/* Share Buttons */}
          <ShareButtons 
            hack={hack} 
            url={`https://daily-health-advice-vdw1.vercel.app/tip/${hack.slug}`} 
          />

          {/* Tags */}
          {hack.tags && hack.tags.length > 0 && (
            <div className="mb-12">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {hack.tags.map((tag, index) => (
                  <Link
                    key={index}
                    to={`/tag/${encodeURIComponent(tag)}`}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800 hover:bg-primary-100 hover:text-primary-700 transition-colors duration-200"
                  >
                    <TagIcon className="h-4 w-4 mr-1" />
                    {tag}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Related Articles */}
          {relatedHacks.length > 0 && (
            <section className="border-t border-gray-200 pt-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-8">Related Daily Tips</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {relatedHacks.map((relatedHack) => (
                  <article key={relatedHack.id} className="health-hack-card group">
                    <div className="mb-4">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(relatedHack.category)}`}>
                        {getCategoryName(relatedHack.category)}
                      </span>
                    </div>
                    
                    <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors duration-200">
                      <Link to={`/tip/${relatedHack.slug}`}>
                        {relatedHack.title}
                      </Link>
                    </h3>
                    
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {relatedHack.excerpt}
                    </p>
                    
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center">
                        <ClockIcon className="h-4 w-4 mr-1" />
                        {relatedHack.read_time} min read
                      </div>
                      <Link
                        to={`/tip/${relatedHack.slug}`}
                        className="text-primary-600 font-medium hover:text-primary-700 transition-colors duration-200"
                      >
                        Read More →
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}
        </article>
      </div>
    </>
  );
};

export default HealthHackPage; 